import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  GetVendors,
  DeleteVendor,
} from "../../../services/GeneralMaintenanceService"; // Import the delete service method
import CommonLoader from "../../common/CommonLoader";
import ViewVendor from "./ViewVendor";
import DeleteConfirmation from "../../common/DeleteConfirmation"; // Import the DeleteConfirmation component
import { NotificationManager } from "react-notifications";
import AddEditVendorDialog from "./AddEditVendorDialog";

const VendorsTable = () => {
  const columns = ["Vendor ID", "Vendor Name", "Actions"];
  const columnHeaders = ["vendorId", "vendorName"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [vendorsData, setVendorsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false); // State for delete confirmation
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null); // State to store the selected item to delete
  const [openDialog, setOpenDialog] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    const filteredResults = vendorsData.filter((vendor) =>
      columnHeaders.some((header) =>
        vendor[header]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, vendorsData]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await GetVendors();
      setVendorsData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleAddVendor = () => {
    setEditVendor(null);
    setOpenDialog(true);
  };
  const handleViewClick = (vendor) => {
    setSelectedVendor(vendor);
  };
  const handleEditClick = (vendor) => {
    setEditVendor(vendor);
    setOpenDialog(true);
  };
  const handleBackClick = () => {
    setSelectedVendor(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (vendor) => {
    setSelectedItemToDelete(vendor);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteVendor(selectedItemToDelete.vendorId);
      setIsDeleteConfirmationOpen(false);
      const updatedVendors = vendorsData.filter(
        (item) => item.vendorId !== selectedItemToDelete.vendorId
      );
      setVendorsData(updatedVendors);
      setFilteredData(updatedVendors);
      NotificationManager.success(
        `${selectedItemToDelete.vendorName} deleted successfully`
      );
    } catch (error) {
      setIsDeleteConfirmationOpen(false);
      console.error("Error deleting vendor:", error);
      NotificationManager.error("Error deleting vendor");
    }
  };

  const renderActions = (vendor) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(vendor)}
      >
        <EditIcon />
      </IconButton>

      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(vendor)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(vendor)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">Vendors </span>
    <hr />
      {selectedVendor ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewVendor vendor={selectedVendor} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddVendor}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((vendor, index) => (
                      <TableRow
                        key={vendor.vendorId}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>{vendor[column]}</TableCell>
                        ))}
                        <TableCell>{renderActions(vendor)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
        </>
      )}

      {/* Render the delete confirmation popup */}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete the ${selectedItemToDelete?.vendorName}?`}
        color="red"
      />
      <AddEditVendorDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={fetchVendors}
        selectedVendor={editVendor}
      />
    </>
  );
};

export default VendorsTable;
